import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Info, ArrowRight } from "lucide-react";
import { PRICING } from "../data/site";

function PriceTable({ title, note, columns, rows, id, dark }) {
  return (
    <div id={id} className="scroll-mt-28 bg-white rounded-3xl border border-[#D6E4DC] overflow-hidden shadow-sm" data-testid={`price-table-${id}`}>
      <div className="bg-brand-emerald px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
        <h3 className="font-display font-bold text-white text-lg sm:text-xl">{title}</h3>
        <p className="text-xs text-brand-gold italic">{note}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-mint text-brand-emerald">
              {columns.map((c) => <th key={c} className="text-left px-6 sm:px-8 py-3 font-bold">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r[0]} className={i % 2 ? "bg-brand-canvas" : "bg-white"}>
                <td className="px-6 sm:px-8 py-3.5 font-semibold text-brand-ink">{r[0]}</td>
                {r.slice(1).map((cell, j) => (
                  <td key={j} className={`px-6 sm:px-8 py-3.5 ${j === r.length - 2 ? "font-bold text-brand-emerald" : "text-brand-muted"}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  return (
    <div className="pt-24 lg:pt-32 pb-20" data-testid="pricing-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Price List</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-emerald tracking-tight">Honest pricing, <span className="text-gradient-gold">in Naira.</span></h1>
        <p className="mt-5 text-brand-muted text-base sm:text-lg max-w-2xl leading-relaxed">
          Every service, every property size, one transparent price list. Pick what fits, book online, and we'll confirm your exact quote.
        </p>
        <div className="mt-6 flex items-start gap-3 bg-brand-gold/10 border border-brand-gold/30 rounded-2xl px-5 py-4 max-w-2xl" data-testid="pricing-note">
          <Info className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
          <p className="text-sm text-brand-ink leading-relaxed">
            <strong>Please note:</strong> All prices listed are starting prices only. Final prices may vary depending on the level of dirt,
            scope of work, size and condition of the space, equipment required, and the time limit.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
        <PriceTable id="residential" {...PRICING.residential} />
        <PriceTable id="monthly" {...PRICING.monthly} />
        <PriceTable id="office" {...PRICING.office} />
        <PriceTable id="move-in-out" {...PRICING.moveInOut} />
        <PriceTable id="event" {...PRICING.event} />
        <PriceTable id="after-party" {...PRICING.afterParty} />
      </section>

      <section id="addons" className="scroll-mt-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white rounded-3xl border border-[#D6E4DC] overflow-hidden shadow-sm" data-testid="price-table-addons">
          <div className="bg-brand-emerald px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
            <h3 className="font-display font-bold text-white text-lg sm:text-xl">Add-On Services</h3>
            <p className="text-xs text-brand-gold italic">Extra care for every space</p>
          </div>
          <div className="grid md:grid-cols-2">
            {PRICING.addons.map((a, i) => (
              <div key={a.name} className={`flex items-center justify-between px-6 sm:px-8 py-3.5 text-sm ${i % 2 ? "bg-brand-canvas" : "bg-white"} border-b border-[#EFF5F1]`}>
                <span className="font-medium text-brand-ink">{a.name}</span>
                <span className="font-bold text-brand-emerald whitespace-nowrap ml-4">{a.price}</span>
              </div>
            ))}
          </div>
          <p className="px-6 sm:px-8 py-4 text-xs text-brand-muted bg-brand-mint">
            Note: Final pricing may vary depending on the size, quantity, condition, and level of cleaning required.
          </p>
        </div>

        <div className="mt-12 hero-gradient noise-overlay relative rounded-3xl px-8 py-12 text-center overflow-hidden">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white relative z-10">Found your service? <span className="text-brand-lime">Book it now.</span></h2>
          <p className="mt-3 text-white/70 relative z-10">Get an instant estimate as you build your booking.</p>
          <Link to="/booking" data-testid="pricing-book-btn" className="relative z-10 mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-lime text-brand-emerald-dark font-bold hover:bg-white transition-all duration-300">
            Start Booking <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
